'use client';

import { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { useSession, signIn, signOut } from 'next-auth/react';
import { AuthStates, ROUTES } from '@/lib/utils';
import { logOut } from '@/features/auth/services/authApi';
import { toast } from 'sonner';
import { LoginLoadingState } from '@/components/common/loading-states';
import { ApiErrorConstants } from '@/lib/constants/error-constant';

interface AuthContextType {
  user?: any;
  token: string | undefined;
  signOut: (query?: Record<string, string>, isBorrower?: boolean) => void;
  signIn: (params?: Record<string, string>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export default function AuthProvider({ children }: { children: ReactNode }) {
  const { data: session } = useSession();
  const [loading, setLoading] = useState<boolean>(false);
  const value: AuthContextType = {
    user: session?.user,
    token: session?.accessToken,
    signOut: async (query, isBorrower) => {
      await logOut();
      let callbackUrl: string = ROUTES.LOGIN;
      if (query) {
        const params = new URLSearchParams(query as Record<string, string>);
        callbackUrl = `${ROUTES.LOGIN}?${params.toString()}`;
      }
      if (isBorrower) {
        callbackUrl = `${callbackUrl}?kc_idp_hint=customer`;
      }
      await signOut({ callbackUrl });
    },
    signIn: (params) => {
      const allParams = params || {};

      const { invoiceId, supplierOrgId, amount, customerName, ...rest } = allParams;

      const cleanParams = Object.fromEntries(Object.entries(rest).filter(([_, v]) => v !== undefined));

      let callbackUrl: string = ROUTES.HOME;

      if (invoiceId) {
        const query = new URLSearchParams({
          invoiceId,
          supplierOrgId,
          amount,
          customerName,
        });

        callbackUrl = `${ROUTES.AUTHORIZEPAYMENT}?${query.toString()}`;
        cleanParams['kc_idp_hint'] = 'customer';
      }

      signIn(
        'keycloak',
        { callbackUrl },
        {
          prompt: 'login',
          ...cleanParams,
        },
      );
    },
  };
  useEffect(() => {
    if (session?.error === ApiErrorConstants.REFRESH_TOKEN_ERROR) {
      toast.error(ApiErrorConstants.SESSION_EXPIRED_MESSAGE);
      setLoading(true);
      signOut({ callbackUrl: ROUTES.LOGIN });
    }
  }, [session]);

  if (loading) {
    return <LoginLoadingState />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export const useAuthReady = () => {
  const { status } = useSession();
  return { enabled: status === AuthStates.AUTHENTICATED };
};
